import React, { useCallback, useEffect, useRef, useState } from "react";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { faDesktop, faMobileNotch, faWindow } from "@fortawesome/pro-regular-svg-icons";
import { getBrandReturnAppDemoBaseUrl, getBrandReturnAppDemoUrl, getBrandReturnAppLiveBaseUrl, getBrandReturnAppLiveUrl, getDefaultLanguage, getSelectedBrandEnabledLanguages } from "~/cores/appCore";
import { TurnrCard, TurnrCardHeader } from "~/components/card/TurnrCard.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { COLORS, IFRAME_ACTIONS, INGRID_IFRAME_ID, INGRID_IFRAME_MODAL_APP_ID, QUERY_PARAMETER_MODAL_IFRAME_ACTIVE } from "rylak-common/src/constants";
import { TurnrFontAwesomeIconButtonRound } from "~/components/buttons/icon-buttons/TurnrFontAwesomeIconButton";
import { CustomImageNew } from "rylak-common/src/image/CustomImage.jsx";
import { TurnrTooltip, TurnrTooltipPortalAnchor } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import "highlight.js/styles/sunburst.min.css";
import hljs from "highlight.js";
import styles from "./IframePart.module.scss";
import { QUERY_MAP } from "~/constants";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";

const PREVIEW_MODES = {
    PC: "PC",
    PHONE: "PHONE",
    IFRAME: "IFRAME",
};

function ResetIFrameButton({ onClick, className, style }) {
    return (
        <TurnrButton className={className} style={style} variant={"text"} onClick={onClick}>
            Reset Demo
        </TurnrButton>
    );
}

function PreviewModeButton({ icon, previewMode, setPreviewMode, previewModeKey, tooltipPortalRef, tooltipText }) {
    const outline = "1px solid " + COLORS.WEAK_TEXT;
    const active = previewMode === previewModeKey;
    return (
        <TurnrGrid item>
            <TurnrTooltip content={tooltipText} placement={"right"} portalRef={tooltipPortalRef}>
                <TurnrCard
                    style={{
                        borderRadius: "50px",
                        outline: active && outline,
                    }}
                >
                    <TurnrFontAwesomeIconButtonRound icon={icon} iconSize={"xxl"} color={!active && COLORS.WEAK_TEXT} onClick={() => setPreviewMode(previewModeKey)} />
                </TurnrCard>
            </TurnrTooltip>
        </TurnrGrid>
    );
}

function ViewButtons({ previewMode, setPreviewMode }) {
    const tooltipPortalRef = useRef();
    return (
        <div>
            <TurnrTooltipPortalAnchor ref={tooltipPortalRef} placement={"bottom"}>
                <TurnrGrid container justifyContent={"center"} spacing={1}>
                    <PreviewModeButton
                        previewMode={previewMode}
                        previewModeKey={PREVIEW_MODES.PC}
                        setPreviewMode={setPreviewMode}
                        icon={faDesktop}
                        tooltipPortalRef={tooltipPortalRef}
                        tooltipText={"PC preview"}
                    />
                    <PreviewModeButton
                        previewMode={previewMode}
                        previewModeKey={PREVIEW_MODES.PHONE}
                        setPreviewMode={setPreviewMode}
                        icon={faMobileNotch}
                        tooltipPortalRef={tooltipPortalRef}
                        tooltipText={"Phone preview"}
                    />
                    <PreviewModeButton
                        previewMode={previewMode}
                        previewModeKey={PREVIEW_MODES.IFRAME}
                        setPreviewMode={setPreviewMode}
                        icon={faWindow}
                        tooltipPortalRef={tooltipPortalRef}
                        tooltipText={"Iframe preview"}
                    />
                </TurnrGrid>
            </TurnrTooltipPortalAnchor>
        </div>
    );
}

const rightSideGridValues = { xs: 12, md: 7, lg: 8, xl: 9 };

const IframePart = function (props) {
    const { appProps, allInputValues, setIFrameData } = props;

    const [previewMode, setPreviewMode] = useQueryState({
        key: QUERY_MAP.APP_DESIGN_PREVIEW,
        defaultValue: PREVIEW_MODES.PHONE,
    });
    const previewIframeRef = React.useRef();
    const previewModalIframeRef = React.useRef();

    const previewModePc = previewMode === PREVIEW_MODES.PC;
    const previewModePhone = previewMode === PREVIEW_MODES.PHONE;
    const previewModeIframe = previewMode === PREVIEW_MODES.IFRAME;

    const [iFrameResetNumber, setIFrameResetNumber] = useState(0);

    const updateIframe = useCallback(() => {
        let newBrandData = { ...appProps?.selectedBrandData };
        Object.entries(allInputValues).forEach(([key, { value }]) => {
            newBrandData[key] = value;
        });
        previewIframeRef?.current?.contentWindow?.postMessage(
            {
                action: IFRAME_ACTIONS.UPDATE_BRAND_DATA,
                data: newBrandData,
                forcePhoneView: previewModePhone,
                forceIframe: previewModeIframe,
                forceModalIframeActive: previewModeIframe,
            },
            getBrandReturnAppDemoBaseUrl(appProps),
        );
    }, [allInputValues, previewIframeRef, previewModeIframe]);

    useEffect(() => {
        window.onmessage = function (e) {
            if (e.data?.action === IFRAME_ACTIONS.INITIALIZED) {
                if (e.data) {
                    setIFrameData(e.data);
                }
                updateIframe();
                // updateIframeNew();
            }
        };
        if (previewIframeRef.current) {
            updateIframe();
            // updateIframeNew();
        }
    }, [
        previewIframeRef,
        updateIframe,
        // updateIframeNew
    ]);

    const resetIframe = useCallback(() => {
        setIFrameResetNumber(iFrameResetNumber + 1);
    }, [previewIframeRef, iFrameResetNumber]);

    useEffect(() => {
        if (previewModeIframe) {
            hljs.highlightAll();
        }
    }, [previewModeIframe]);

    const queryLanguageExampleCode = `?lang=${getDefaultLanguage(appProps)}`;
    const modalIframeQueryParam = `${QUERY_PARAMETER_MODAL_IFRAME_ACTIVE}=true`;

    function getLanguageSelectCodeSnippet() {
        return `<!-- Set language -->
<label for="language-select">Language select outside Iframe</label>
<select id="language-select" onchange="changeLanguageFn()">
${getSelectedBrandEnabledLanguages(appProps)
    .map((languageCode) => {
        return `<option${getDefaultLanguage(appProps) === languageCode ? " selected=true" : ""} value=${languageCode}>${languageCode}</option>`;
    })
    .slice(0, 3)
    .join("\n")}
</select>`;
    }

    function getIframeCodeSnippet({ languageQueryActive = false } = {}) {
        return `<!-- The main Iframe running the Return Portal -->
<iframe
  frameBorder="0"
  id="${INGRID_IFRAME_ID}"
  src="${getBrandReturnAppLiveUrl(appProps)}${languageQueryActive ? `${queryLanguageExampleCode}&${modalIframeQueryParam}"></iframe> <!-- Query language & Modal Iframe -->` : '"'}`;
    }

    function getIframeModalCodeSnippet() {
        return `<!-- Modal Iframe -->
<iframe
  frameBorder="0"
  id="${INGRID_IFRAME_MODAL_APP_ID}"
  style="width: 0; height:0; visibility: hidden; position: fixed; left: 0; top:0; z-index: 10000"
  src="${getBrandReturnAppLiveUrl(appProps) + "/iframe-modal"}"
></iframe>`;
    }

    function getCssSnippet({ dynamicHeight = false } = {}) {
        return `<!-- Example CSS -->\n<!-- Recommended maximum width: 768px --> ${dynamicHeight ? "\n<!-- The height will be set dynamically -->" : ""}
<style>
  #${INGRID_IFRAME_ID} {
    width: 100%;
    height: ${dynamicHeight ? "0px" : "800px"};
  }
  @media (min-width: 768px) {
    #${INGRID_IFRAME_ID} {
      width: 600px;
    }
  }
</style>`;
    }

    const javascriptSnippetDynamicHeight = `<!-- Dynamic height -->
<script>
  window.addEventListener('message', function(event) {
      const iframe = window.document.getElementById("${INGRID_IFRAME_ID}");
      if(event.source === iframe?.contentWindow && event?.data?.action === "${IFRAME_ACTIONS.APP_SIZE_CHANGE}") {
        iframe.style.height = \`\${event?.data?.height}px\`;
    }
  });
</script>`;

    const javascriptSnippetScrollToView = `<!-- Scroll to view -->
<script>
  window.addEventListener('message', function(event) {
    const iframe = window.document.getElementById("${INGRID_IFRAME_ID}");
    // We don't want to scroll to view on login page because we do not want to trigger the scroll-to-view before the customer interacts with the Iframe
      if (event.source === iframe?.contentWindow && event?.data?.action === "${IFRAME_ACTIONS.PAGE_CHANGE}" && event?.data?.page !== "LOGIN") {
            if (iframe.getBoundingClientRect().bottom > window.innerHeight) {
                iframe.scrollIntoView(false);
            }

            if (iframe.getBoundingClientRect().top < 0) {
                iframe.scrollIntoView();
            }
        }
  });
</script>`;

    const javascriptSnippetModalIframe = `<!-- Modal Iframe -->
<script>
  window.addEventListener('message', function(event) {
    const mainIframe = window.document.getElementById("${INGRID_IFRAME_ID}");
    const modalIframe = window.document.getElementById("${INGRID_IFRAME_MODAL_APP_ID}");

    const isFromMainIframe = event.source === mainIframe?.contentWindow;
    const isFromModalIframe = event.source === modalIframe?.contentWindow;

    // Allows internal communication between Iframes
    if (event?.data?.relayToOtherIframe) {
        const toIframe = isFromMainIframe ? modalIframe : mainIframe;
        toIframe?.contentWindow?.postMessage(event?.data, "${getBrandReturnAppLiveBaseUrl(appProps)}");
        return;
    }

    // When the modal Iframe opens, you need to make it fullscreen
    if (isFromModalIframe && event?.data?.action === "${IFRAME_ACTIONS.OPEN_MODAL}") {
        // Unless the height on your body is set to 100dvh/100vh you will probably need some code to make sure the background does not scroll when the popup is up
        // The follwoing line is just an example that would work for many web pages. But depending on how your website is built, the solution might vary
        document.body.style.overflowY = "hidden";
        modalIframe.style.width = "100vw";
        modalIframe.style.height = "100dvh";
        modalIframe.style.visibility = "initial";
    }

    // And fully hide it when it closes
    if (isFromModalIframe && event?.data?.action === "${IFRAME_ACTIONS.CLOSE_MODAL}") {
        document.body.style.overflowY = "auto";
        modalIframe.style.width = "0";
        modalIframe.style.height = "0";
        modalIframe.style.visibility = "hidden";
    }
  });
</script>`;

    const javascriptChangeLanguageSnippet = `<!-- Set language -->
<script>
  function changeLanguageFn() {
    const languageCode = document.getElementById("language-select").value;
    document.getElementById("${INGRID_IFRAME_ID}").contentWindow.postMessage({
      action: "${IFRAME_ACTIONS.SET_LANGUAGE_CODE}",
      data: {
           languageCode: languageCode}
      }, "${getBrandReturnAppLiveBaseUrl(appProps)}"
    )}
  </script>`;

    function formatSnippet(text, nrOfSpaces = 4) {
        const spaces = new Array(nrOfSpaces + 1).join(" ");
        return spaces + text.replaceAll("\n", "\n" + spaces);
    }

    const minimumSetupSnippet = `<html>
  <head>

${formatSnippet(getCssSnippet())}

  </head>
  <body>

${formatSnippet(getIframeCodeSnippet())}

  </body>
</html>
`;

    const exampleComplexSetupSnippet = `<html>
  <head>

${formatSnippet(javascriptSnippetDynamicHeight)}

${formatSnippet(javascriptSnippetScrollToView)}

${formatSnippet(javascriptChangeLanguageSnippet)}

${formatSnippet(javascriptSnippetModalIframe)}

${formatSnippet(getCssSnippet({ dynamicHeight: true }))}

  </head>
  <body>

${formatSnippet(getLanguageSelectCodeSnippet())}

${formatSnippet(getIframeCodeSnippet({ languageQueryActive: true }))}

${formatSnippet(getIframeModalCodeSnippet({ languageQueryActive: true }))}

  </body>
</html>
`;

    const [iframeHeight, setIframeHeight] = useState(500);
    useEffect(() => {
        window.addEventListener("message", function (event) {
            const action = event?.data?.action || event?.data?.messageType;
            if (action === IFRAME_ACTIONS.APP_SIZE_CHANGE || action === IFRAME_ACTIONS.APP_SIZE_CHANGE_OLD) {
                setIframeHeight(event?.data?.height);
            }
        });
    }, []);

    const listenerFunction = useCallback(
        (event) => {
            const mainIframe = window.document.getElementById(INGRID_IFRAME_ID);
            const modalIframe = window.document.getElementById(INGRID_IFRAME_MODAL_APP_ID);

            const isFromMainIframe = event.source === mainIframe?.contentWindow;
            const isFromModalIframe = event.source === modalIframe?.contentWindow;

            // Allows internal communication between Iframes
            if (event?.data?.relayToOtherIframe) {
                const toIframe = isFromMainIframe ? previewModalIframeRef : previewIframeRef;
                toIframe?.current?.contentWindow?.postMessage(event?.data, getBrandReturnAppDemoBaseUrl(appProps));

                return;
            }

            // When the modal Iframe opens, you need to make it fullscreen
            if (isFromModalIframe && event?.data?.action === IFRAME_ACTIONS.OPEN_MODAL) {
                modalIframe.style.width = "100vw";
                modalIframe.style.height = "100dvh";
                modalIframe.style.visibility = "initial";
            }

            // And fully hide it when it closes
            if (isFromModalIframe && event?.data?.action === IFRAME_ACTIONS.CLOSE_MODAL) {
                modalIframe.style.width = "0";
                modalIframe.style.height = "0";
                modalIframe.style.visibility = "hidden";
            }
        },
        [previewIframeRef, previewModalIframeRef],
    );

    useEffect(() => {
        window.addEventListener("message", listenerFunction);
        return () => {
            window.removeEventListener("message", listenerFunction);
        };
    }, [listenerFunction]);

    function downloadHtmlFile({ name, content }) {
        const link = document.createElement("a");
        const file = new Blob([content], { type: "text/plain" });
        link.href = URL.createObjectURL(file);
        link.download = name + ".html";
        link.click();
        URL.revokeObjectURL(link.href);
    }

    function downloadSimpleExample() {
        downloadHtmlFile({ name: "minimumExample", content: minimumSetupSnippet });
    }

    function downloadAdvancedExample() {
        downloadHtmlFile({ name: "advancedExample", content: exampleComplexSetupSnippet });
    }

    return (
        <TurnrGrid item container {...rightSideGridValues} spacing={1}>
            <TurnrGrid item xs={12}>
                <div className={styles["reset-preview-wrapper"]}>
                    <ResetIFrameButton className={styles["first-reset-iframe-btn"]} onClick={resetIframe} />
                    <ViewButtons previewMode={previewMode} setPreviewMode={setPreviewMode} />
                    <div className={styles["second-reset-iframe-btn"]}>
                        <ResetIFrameButton
                            // Only to make sure things are centered
                            style={{ visibility: "hidden" }}
                            onClick={resetIframe}
                        />
                    </div>
                </div>
            </TurnrGrid>
            <TurnrGrid item xs={12}>
                <div
                    style={{
                        margin: "auto",
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                    }}
                >
                    {previewModePhone && (
                        <div style={{ position: "absolute", top: 0 }}>
                            <CustomImageNew fixedWidth={462} fixedHeight={921} style={{ objectFit: "fill" }} src="/static/images/phone.svg" />
                        </div>
                    )}
                    <div
                        style={
                            previewModePhone
                                ? {
                                      position: "relative",
                                      width: "400px",
                                      paddingTop: "67px",
                                      height: "805px",
                                      display: "flex",
                                      margin: "auto",
                                  }
                                : previewModePc
                                  ? {
                                        border: "10px solid #111111",
                                        borderRadius: "8px",
                                        width: "100%",
                                        height: "900px",
                                        display: "flex",
                                        margin: "auto",
                                    }
                                  : {
                                        // border: "10px solid #111111",

                                        borderRadius: "8px",
                                        height: iframeHeight + 1,
                                        width: "100%",
                                        maxWidth: "600px",
                                        display: "flex",
                                        margin: "auto",
                                        zIndex: 1,
                                    }
                        }
                    >
                        <iframe
                            id={INGRID_IFRAME_ID}
                            ref={previewIframeRef}
                            key={iFrameResetNumber}
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                margin: "auto",
                                border: "none",
                                // boxShadow: "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px"
                            }}
                            src={getBrandReturnAppDemoUrl(
                                appProps,
                                {
                                    iframeMode: false,
                                }, // handled in postMessage instead
                            )}
                            title="Turnr iframe"
                        ></iframe>
                        <iframe
                            id={INGRID_IFRAME_MODAL_APP_ID}
                            ref={previewModalIframeRef}
                            frameBorder="0"
                            style={{
                                width: 0,
                                height: 0,
                                visibility: "hidden",
                                position: "fixed",
                                left: 0,
                                top: 0,
                                zIndex: 100000,
                            }}
                            src={getBrandReturnAppDemoUrl(appProps, {
                                path: "/iframe-modal",
                            })}
                        ></iframe>
                    </div>
                    {previewModeIframe && (
                        <TurnrGrid container spacing={2} className={"pt2"}>
                            <TurnrGrid item xs={12}>
                                <TurnrCard
                                    fullWidth
                                    style={{
                                        width: "100%",
                                    }}
                                >
                                    <TurnrCardHeader
                                        title={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: 10,
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                Minimum setup example{" "}
                                                <TurnrButton size={"small"} onClick={downloadSimpleExample}>
                                                    Download Example
                                                </TurnrButton>
                                            </div>
                                        }
                                        subTitle={"Fixed height iframe and no communication between between your homepage and the iframe"}
                                    ></TurnrCardHeader>
                                    <pre style={{ margin: 0 }}>
                                        <code>{minimumSetupSnippet}</code>
                                    </pre>
                                </TurnrCard>
                            </TurnrGrid>
                            <TurnrGrid item xs={12}>
                                <TurnrCard
                                    fullWidth
                                    style={{
                                        width: "100%",
                                    }}
                                >
                                    <TurnrCardHeader
                                        title={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: 10,
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                All features setup example{" "}
                                                <TurnrButton size={"small"} onClick={downloadAdvancedExample}>
                                                    Download Example
                                                </TurnrButton>
                                            </div>
                                        }
                                        subTitle={
                                            <div>
                                                <ul>
                                                    <li>
                                                        <b>Dynamic height</b>
                                                        <br />
                                                        You will never get a scroll for the iframe. The iframe will tell your app what height it requires. See example code below
                                                        <br />
                                                        <br />
                                                        <b>Scroll to view</b>
                                                        <br />
                                                        When navigating the Return Portal in the Iframe the Iframe might visually disappear because of huge height difference between pages, especially
                                                        on really big orders with lots of products. This logic auto scrolls the Iframe into view when navigating it.
                                                        <br />
                                                        <br />
                                                        <b>Query language</b>
                                                        <br />
                                                        You can force the initial language using a query parameter to the Iframe. Set to <b>{queryLanguageExampleCode}</b> in the example below. Note
                                                        that when setting this query parameter, the language selected inside the Iframe is removed
                                                        <br />
                                                        <br />
                                                        <b>Set language</b>
                                                        <br />
                                                        You can set the language by communicating with the Iframe, so that when the language changes on your site it also changes in the Iframe. See an
                                                        example setup below
                                                        <br />
                                                        <br />
                                                        <b>Modal Iframe</b>
                                                        <br />
                                                        Popups in iframes look bad because they are restricted by the size of the iframe. With this solution another iframe is responsible over the
                                                        popups by taking full screen space. You need to add a second Iframe and some javascript. See example below. Don't forget to set this query
                                                        parameter in the main Iframe: <b>{modalIframeQueryParam}</b>
                                                    </li>
                                                </ul>
                                            </div>
                                        }
                                    ></TurnrCardHeader>

                                    <pre style={{ margin: 0 }}>
                                        <code>{exampleComplexSetupSnippet}</code>
                                    </pre>
                                </TurnrCard>
                            </TurnrGrid>
                        </TurnrGrid>
                    )}
                    {previewModePc && (
                        <div
                            style={{
                                position: "relative",
                                width: "400px",
                                height: "140px",
                            }}
                        >
                            <CustomImageNew fill style={{ objectFit: "fill" }} src="/static/images/monitor-stand.svg"></CustomImageNew>
                        </div>
                    )}
                </div>
            </TurnrGrid>
        </TurnrGrid>
    );
};

export default IframePart;
